import { LitElement, html } from 'lit';

import eventbus from '../../utils/eventbus';
import tickerObserver from '../../utils/ticker/ticker-observer';
import translate from '../../utils/webcomponents/translate';
import portfolioEvents from './portfolio-events';
import watchlistEvents from '../watchlist/watchlist-events';
import alertEvents from '../alert/alert-events';
import { createGtmPushData } from '../../tracking/utils';

import Dropdown from '../dropdown';
import Tooltip from '../tooltip';

import '../broker-dropdown';
import '../icon';
import '../toggle';

const STOPLOSS_TOGGLE_NAME = 'stoplossAlert';

const renderWatchlistButton = (clickCallback, isInWatchlist) => {
	const labelAddToWatchlist = translate('portfolio', 'addToWatchlist', 'Add to watchlist');
	const labelRemoveFromWatchlist = translate('portfolio', 'removeFromWatchlist', 'Remove from watchlist');
	const tooltipText = isInWatchlist ? labelRemoveFromWatchlist : labelAddToWatchlist;
	const icon = isInWatchlist ? 'watchlist-fill' : 'watchlist-empty';

	return html`
		<div class="manageable-list__item-button">
			<button
				class="button button--ghost button--icon-only button--small ${isInWatchlist ? 'is-active' : ''}"
				@click="${clickCallback}"
				data-option-tooltip-text="${tooltipText}"
				data-option-label-in-watchlist="${labelRemoveFromWatchlist}"
				data-option-label-not-in-watchlist="${labelAddToWatchlist}"
			>
				<span class="button__icon">
					<x-icon icon="${icon}"></x-icon>
				</span>
			</button>
		</div>
	`;
};

const renderBrokerDropdown = (isin) => {
	const labelBuy = translate('portfolio', 'buy', 'Buy');

	return html`
		<div
			class="manageable-list__item-button"
			data-option-tooltip-text="${labelBuy}"
		>
			<x-broker-dropdown
				class="broker-dropdown"
				id="${isin}"
				.isin="${isin}"
			></x-broker-dropdown>
		</div>
	`;
};

const renderDropdownItem = (icon, clickCallback, label) => html`
	<button
		class="dropdown__item dropdown__item--padded has-js"
		@click="${clickCallback}"
	>
		<div class="dropdown__visual">
			<span class="button__icon">
				<x-icon icon="${icon}"></x-icon>
			</span>
		</div>
		<span class="dropdown__label">${label}</span>
	</button>
`;

const renderNameAndIsin = (productName, isin, showIsinColumn) =>
	showIsinColumn
		? html`${productName}`
		: html`
				${productName}
				<br />
				<span class="manageable-list__item-small">${isin}</span>
			`;

const renderStoploss = (data) => {
	const labelStoplossAlert = translate('portfolio', 'stoplossAlert', 'Stop loss alert');

	return html`
		<div class="dropdown__item dropdown__item--padded has-js">
			<div class="dropdown__visual">
				<x-toggle
					id="${data.id}"
					name="${STOPLOSS_TOGGLE_NAME}"
					value="${data.id}"
					iconOn="alert"
					iconOff="alert-disabled"
					?isChecked="${data.hasStopLossLevelReachedNotification}"
					?isDisabled=${data.isProductInactive}
				>
				</x-toggle>
			</div>
			<label
				class="dropdown__label"
				for="toggle-${data.id}"
			>
				${labelStoplossAlert}
			</label>
		</div>
	`;
};

const renderCopyIsin = (isin) => html`
	<div class="manageable-list__item-copy-isin">
		${isin}
		<x-copy-value-button
			.value="${isin}"
			.labelAction="${translate('watchlist', 'copy', 'Copy')}"
			.labelSuccess="${translate('watchlist', 'copied', 'Copied')}"
		></x-copy-value-button>
	</div>
`;

const getSentimentClass = (value) => {
	if (!value) {
		return '';
	}

	return value > 0 ? 'is-positive' : 'is-negative';
};

class PortfolioItem extends LitElement {
	static get properties() {
		return {
			data: { type: Object },
			isWatchlistActive: { type: Boolean },
			showIsinColumn: { type: Boolean },
			showCurrencyColumn: { type: Boolean },
		};
	}

	constructor() {
		super();

		this.tooltips = null;

		this.onTick = this.onTick.bind(this);
		this.onChange = this.onChange.bind(this);
	}

	firstUpdated() {
		super.firstUpdated();

		// trick to make bid/ask unresponsive because the bid/ask
		// gets updated by `ticker-element`. When the element get's updated
		// by `ticker-element` first and later by `portfolio`, lit-element trows an error
		this.bid = this.data.bid;
		this.ask = this.data.ask;

		tickerObserver.addTickers(this);

		eventbus.on(`tick:${this.data.isin}`, this.onTick);

		this.addEventListener('change', this.onChange);

		this.dropdown = new Dropdown(this.querySelector('.js-actions')).start();

		this.createTooltips();

		this.requestUpdate();
	}

	disconnectedCallback() {
		if (this.isDragging) {
			return;
		}

		tickerObserver.removeTickers(this);
		eventbus.off(`tick:${this.data.isin}`, this.onTick);

		this.removeEventListener('change', this.onChange);

		super.disconnectedCallback();
	}

	onChange({ target }) {
		const { name, checked } = target;

		if (name === STOPLOSS_TOGGLE_NAME) {
			this.updateStopLossAlert(checked);
		}
	}

	render() {
		const { data } = this;

		const labelAddAlert = translate('portfolio', 'addAlert', 'Add an alert');
		const labelDelete = translate('portfolio', 'delete', 'Delete');
		const labelEdit = translate('portfolio', 'edit', 'Edit');

		const exchangeLabelMarkup = data.showExchangeIcon
			? html`&nbsp;<span class="tag">${data.exchangeName}</span>`
			: '';

		return html`
			<div class="table__cell manageable-list__item-action">
				<button
					class="button button--ghost button--icon-only button--small button--drag"
					aria-controls="drag"
				>
					<x-icon icon="drag"></x-icon>
				</button>

				<div
					class="dropdown js-actions"
					data-option-placement="right"
				>
					<button
						class="button button--ghost button--icon-only button--small"
						aria-controls="menu-actions"
					>
						<x-icon icon="menu-kebab"></x-icon>
					</button>

					<div
						class="dropdown__list"
						id="menu-actions"
						role="menu"
					>
						${renderDropdownItem('alert', this.addAlert, labelAddAlert)} ${renderStoploss(data)}
						${renderDropdownItem('edit', this.editItem, labelEdit)}
						${renderDropdownItem('delete', this.removeItem, labelDelete)}
					</div>
				</div>

				${!data.isProductInactive && this.isWatchlistActive
					? renderWatchlistButton(this.onToggleWatchlist, data.isInWatchlist)
					: ''}
				${!data.isProductInactive && data.showTransactionButton ? renderBrokerDropdown(data.isin) : ''}
			</div>
			${this.showIsinColumn ? html`<div class="table__cell">${renderCopyIsin(data.isin)}</div>` : ''}
			<div class="table__cell">
				<a
					class="manageable-list__item-link"
					href="${data.url}"
				>
					${renderNameAndIsin(data.productName, data.isin, this.showIsinColumn)}${exchangeLabelMarkup}
				</a>
			</div>
			<div class="table__cell text-right">${data.purchasePriceFormatted}</div>
			<div class="table__cell text-right">${data.amount}</div>
			<div class="table__cell text-right">${data.investedValueFormatted}</div>

			${this.showCurrencyColumn ? html`<div class="table__cell">${data.currency}</div>` : ''}

			<div class="table__cell text-right">${data.stopLossFormatted}</div>

			<div class="table__cell text-right">
				<span
					class="quote"
					data-isin="${data.isin}"
					data-feed-property="Bid"
					data-has-indicator="true"
				>
					${data.bidFormatted}
				</span>
			</div>
			<div class="table__cell text-right">
				<span
					class="quote"
					data-isin="${data.isin}"
					data-feed-property="Ask"
					data-has-indicator="true"
				>
					${data.askFormatted}
				</span>
			</div>

			<div class="table__cell text-right">${data.currentValueFormatted}</div>

			<div class="table__cell quote text-right ${getSentimentClass(data.profit)}">
				<span class="quote__value"> ${data.profitMonetaryFormatted} (${data.profitFormatted}) </span>
			</div>
		`;
	}

	createTooltips() {
		const tooltips = Array.from(this.querySelectorAll('[data-option-tooltip-text]'));

		this.tooltips = tooltips.map((tooltip) => new Tooltip(tooltip).start());
	}

	updateTooltipForWatchlist(target) {
		const tooltip = this.tooltips.find((t) => t.reference === target);

		if (tooltip) {
			const labelAddToWatchlist = target.dataset.optionLabelNotInWatchlist;
			const labelRemoveFromWatchlist = target.dataset.optionLabelInWatchlist;
			const content = this.data.isInWatchlist ? labelRemoveFromWatchlist : labelAddToWatchlist;

			tooltip.setContent(content);
		}
	}

	removeItem() {
		const gtmPushData = this.createGtmPushData('Delete from Portfolio', 'Portfolio Actions');

		this.dispatchCustomEvent(portfolioEvents.ITEM_REMOVE, { ...this.data, gtmPushData });
	}

	createGtmPushData(action, category) {
		const { isin, productName: name } = this.data;
		const items = { isin, name };

		return createGtmPushData('portfolio_actions', category, action, { items });
	}

	editItem() {
		this.dispatchCustomEvent(portfolioEvents.ITEM_EDIT, this.data);
	}

	updateStopLossAlert(hasStopLossLevelReachedNotification) {
		this.data.hasStopLossLevelReachedNotification = hasStopLossLevelReachedNotification;

		this.dispatchCustomEvent(portfolioEvents.ITEM_STOPLOSS_ALERT, this.data);
	}

	addAlert() {
		const {
			data: { id: portfolioItemId, isin },
			bid,
		} = this;

		this.dispatchCustomEvent(alertEvents.EDIT, {
			portfolioItemId,
			isin,
			threshold: bid,
		});
	}

	onToggleWatchlist(e) {
		this.data.isInWatchlist = !this.data.isInWatchlist;

		this.updateTooltipForWatchlist(e.target);

		this.dispatchCustomEvent(watchlistEvents.ITEM_TOGGLE, this.data);
	}

	onTick(tick) {
		this.dispatchCustomEvent(portfolioEvents.ITEM_TICK, { ...this.data, tick });

		this.requestUpdate();
	}

	dispatchCustomEvent(eventName, detail = {}) {
		const event = new CustomEvent(eventName, {
			bubbles: true,
			detail,
		});

		this.dispatchEvent(event);
	}

	createRenderRoot() {
		return this;
	}
}

customElements.define('x-portfolio-item', PortfolioItem);

export default PortfolioItem;
